import { useEffect, useState } from 'react'
import type { EventConfig } from '@/types'
import { Plus, Trash2 } from 'lucide-react'

interface ScreensaverSettingsProps {
  event: EventConfig
  onChange: (updates: Partial<EventConfig>) => void
}

export default function ScreensaverSettings({ event, onChange }: ScreensaverSettingsProps) {
  const { capture } = event
  const [newPhrase, setNewPhrase] = useState('')
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])

  useEffect(() => {
    if (capture.attractLoop?.wakeMethod !== 'motion') return
    let cancelled = false
    ;(async () => {
      try {
        // Labels are empty until camera permission has been granted once
        const first = await navigator.mediaDevices.enumerateDevices()
        if (!first.some((d) => d.kind === 'videoinput' && d.label)) {
          await navigator.mediaDevices.getUserMedia({ video: true }).then((s) => s.getTracks().forEach((t) => t.stop())).catch(() => {})
        }
        const devs = await navigator.mediaDevices.enumerateDevices()
        if (!cancelled) setCameras(devs.filter((d) => d.kind === 'videoinput'))
      } catch {
        if (!cancelled) setCameras([])
      }
    })()
    return () => { cancelled = true }
  }, [capture.attractLoop?.wakeMethod])

  const updateAttractLoop = (updates: any) => {
    onChange({
      capture: {
        ...capture,
        attractLoop: {
          ...capture.attractLoop,
          ...updates
        }
      }
    })
  }

  const addPhrase = () => {
    if (!newPhrase.trim()) return
    const phrases = [...(capture.attractLoop?.phrases || [])]
    phrases.push(newPhrase.trim())
    updateAttractLoop({ phrases })
    setNewPhrase('')
  }

  const removePhrase = (index: number) => {
    const phrases = [...(capture.attractLoop?.phrases || [])]
    phrases.splice(index, 1)
    updateAttractLoop({ phrases })
  }

  const updatePhrase = (index: number, value: string) => {
    const phrases = [...(capture.attractLoop?.phrases || [])]
    phrases[index] = value
    updateAttractLoop({ phrases })
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-bold mb-1">Screensaver (Attract Loop)</h2>
        <p className="text-sm text-muted-foreground">Configure the video and overlays that play when the booth is idle.</p>
      </div>

      <div className="bg-card rounded-xl border border-border p-6 space-y-6">
        <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer hover:bg-muted transition-colors">
          <input 
            type="checkbox" 
            checked={capture.attractLoop?.enabled || false} 
            onChange={(e) => updateAttractLoop({ enabled: e.target.checked })} 
          />
          <div>
            <div className="text-sm font-medium">Enable Attract Loop</div>
            <div className="text-xs text-muted-foreground">Play a video when no one is using the booth.</div>
          </div>
        </label>

        {capture.attractLoop?.enabled && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium">Idle Timeout</label>
              <div className="flex items-center gap-4">
                <input 
                  type="range" 
                  min="5" 
                  max="300" 
                  value={capture.attractLoop?.timeout || 60} 
                  onChange={(e) => updateAttractLoop({ timeout: parseInt(e.target.value) })} 
                  className="flex-1"
                />
                <span className="text-sm font-mono w-12 text-right">{capture.attractLoop?.timeout || 60}s</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Wake Method</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => updateAttractLoop({ wakeMethod: 'touch' })}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors ${(capture.attractLoop?.wakeMethod === 'touch' || !capture.attractLoop?.wakeMethod) ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background hover:bg-muted'}`}
                >
                  Touch Based
                </button>
                <button
                  onClick={() => updateAttractLoop({ wakeMethod: 'motion' })}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors ${capture.attractLoop?.wakeMethod === 'motion' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background hover:bg-muted'}`}
                >
                  Motion Based
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Video Path</label>
              <input 
                type="text" 
                value={capture.attractLoop?.videoAsset || '/video/bg_video.mp4'} 
                onChange={(e) => updateAttractLoop({ videoAsset: e.target.value })} 
                className="w-full p-2 border rounded-md bg-background text-foreground text-sm"
                placeholder="/video/bg_video.mp4"
              />
              <p className="text-xs text-muted-foreground">Path to the video file in the public folder.</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Max Messages on Screen</label>
              <div className="flex items-center gap-4">
                <input 
                  type="range" 
                  min="1" 
                  max="10" 
                  value={capture.attractLoop?.maxMessages || 4} 
                  onChange={(e) => updateAttractLoop({ maxMessages: parseInt(e.target.value) })} 
                  className="flex-1"
                />
                <span className="text-sm font-mono w-12 text-right">{capture.attractLoop?.maxMessages || 4}</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Interval Between Messages</label>
              <div className="flex items-center gap-4">
                <input 
                  type="range" 
                  min="0.5" 
                  max="10" 
                  step="0.5"
                  value={capture.attractLoop?.intervalSeconds || 2.5} 
                  onChange={(e) => updateAttractLoop({ intervalSeconds: parseFloat(e.target.value) })} 
                  className="flex-1"
                />
                <span className="text-sm font-mono w-12 text-right">{capture.attractLoop?.intervalSeconds || 2.5}s</span>
              </div>
            </div>

            {capture.attractLoop?.wakeMethod === 'motion' && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Motion Camera</label>
                <select
                  value={capture.attractLoop?.motionCameraDeviceId || ''}
                  onChange={(e) => updateAttractLoop({ motionCameraDeviceId: e.target.value })}
                  className="w-full p-2 border rounded-md bg-background text-foreground text-sm"
                >
                  <option value="">Same as photobooth camera</option>
                  {cameras.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(0, 5)}`}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">Camera used only to detect people for waking the screensaver, e.g. the built-in webcam while the photobooth uses a USB camera. Camera IDs can differ per machine, so re-check this on each kiosk.</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Motion Sensitivity</label>
              <div className="flex items-center gap-4">
                <input 
                  type="range" 
                  min="1" 
                  max="100" 
                  value={capture.attractLoop?.motionSensitivity || 50} 
                  onChange={(e) => updateAttractLoop({ motionSensitivity: parseInt(e.target.value) })} 
                  className="flex-1"
                />
                <span className="text-sm font-mono w-12 text-right">{capture.attractLoop?.motionSensitivity || 50}%</span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Hype Phrases</label>
                <p className="text-xs text-muted-foreground">Phrases that will pop up randomly on top of the video.</p>
              </div>

              <div className="space-y-2">
                {(capture.attractLoop?.phrases || []).map((phrase, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input 
                      type="text" 
                      value={phrase} 
                      onChange={(e) => updatePhrase(index, e.target.value)} 
                      className="flex-1 p-2 border rounded-md bg-background text-foreground text-sm"
                    />
                    <button 
                      onClick={() => removePhrase(index)} 
                      className="p-2 text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                      title="Remove phrase"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <input 
                  type="text" 
                  value={newPhrase} 
                  onChange={(e) => setNewPhrase(e.target.value)} 
                  className="flex-1 p-2 border rounded-md bg-background text-foreground text-sm"
                  placeholder="Add a new phrase..."
                  onKeyDown={(e) => e.key === 'Enter' && addPhrase()}
                />
                <button 
                  onClick={addPhrase} 
                  className="p-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
                  title="Add phrase"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
